import React, { useEffect, useState } from "react";
import { Download, Share, PlusSquare, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const T = {
  fr: {
    androidTitle: "Installer GEM PRICE",
    androidBody: "Ajoutez l'app à votre écran d'accueil pour un accès en 1 tap, même hors ligne.",
    install: "Installer",
    later: "Plus tard",
    iosTitle: "Installer GEM PRICE sur iPhone",
    iosStep1: "Touchez l'icône Partager",
    iosStep2: "Choisissez \"Sur l'écran d'accueil\"",
    close: "Fermer",
  },
  en: {
    androidTitle: "Install GEM PRICE",
    androidBody: "Add the app to your home screen for 1-tap access, even offline.",
    install: "Install",
    later: "Later",
    iosTitle: "Install GEM PRICE on iPhone",
    iosStep1: "Tap the Share icon",
    iosStep2: "Choose \"Add to Home Screen\"",
    close: "Close",
  },
};

const DISMISSED_KEY = "gemPriceInstallDismissed";

export const InstallPWA = () => {
  const { lang } = useLang();
  const t = T[lang];
  const [deferred, setDeferred] = useState(null);
  const [showAndroid, setShowAndroid] = useState(false);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    const dismissedAt = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    // Re-show after 7 days
    if (dismissedAt && Date.now() - dismissedAt < 7 * 24 * 3600 * 1000) return;

    const ua = navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = isIos && /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);

    if (isIos && isSafari) {
      const tm = setTimeout(() => setShowIos(true), 4000);
      return () => clearTimeout(tm);
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShowAndroid(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch {}
    setDeferred(null);
    setShowAndroid(false);
  };
  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setShowAndroid(false);
    setShowIos(false);
  };

  if (!showAndroid && !showIos) return null;

  return (
    <div
      data-testid="install-pwa-banner"
      className="fixed left-1/2 -translate-x-1/2 bottom-4 z-50 w-[min(94vw,420px)] rounded-sm border border-[#C1A661]/40 bg-[#0A0F1F]/95 backdrop-blur px-4 py-4 shadow-[0_10px_40px_rgba(0,0,0,0.55)]"
      style={{ marginBottom: "env(safe-area-inset-bottom)" }}
    >
      <button
        onClick={dismiss}
        aria-label={t.close}
        data-testid="install-pwa-close"
        className="absolute top-2 right-2 text-[#8A8F99] hover:text-[#E2CFA4] p-1"
      >
        <X size={16} />
      </button>
      <div className="flex items-center gap-3">
        <img src="/brand/gemprice-icon-64.png" alt="" width="42" height="42" className="rounded-md shrink-0" />
        <div className="flex-1 min-w-0">
          {showAndroid ? (
            <>
              <div className="text-[13px] font-medium text-[#E2CFA4]">{t.androidTitle}</div>
              <div className="text-[11px] text-[#A1A7B3] mt-0.5 leading-snug">{t.androidBody}</div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={install}
                  data-testid="install-pwa-install-btn"
                  className="flex items-center gap-1.5 rounded-full bg-[#C1A661] hover:bg-[#E2CFA4] text-[#050B1E] px-3.5 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold"
                >
                  <Download size={12} strokeWidth={1.8} />
                  {t.install}
                </button>
                <button
                  onClick={dismiss}
                  className="text-[10px] tracking-[0.2em] uppercase text-[#A1A7B3] hover:text-[#E2CFA4] px-2"
                >
                  {t.later}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-[13px] font-medium text-[#E2CFA4]">{t.iosTitle}</div>
              <ol className="mt-2 space-y-1 text-[11px] text-[#A1A7B3]">
                <li className="flex items-center gap-2">
                  <span className="text-[#C1A661]">1.</span>
                  <Share size={13} className="text-[#C1A661]" /> {t.iosStep1}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#C1A661]">2.</span>
                  <PlusSquare size={13} className="text-[#C1A661]" /> {t.iosStep2}
                </li>
              </ol>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
